import { getAdminProfile, updateDisplayName } from "apis/adminServices/adminSettingsServices";
import { forceLogout } from "apis/axios-client";
import { EmailAuthProvider, getAuth, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { Check, Eye, EyeOff, Pencil, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useToastStore } from "store/toastStore";
import { useUserStore } from "store/userStore";
import "../../../assets/css/adminSettings.css";

const Settings: React.FC = () => {
  const [fullName, setFullName] = useState("");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const { username } = useUserStore((state) => state);
  const [tempName, setTempName] = useState(username);
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState<string>(username || "");

  // Password validation states
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecial, setHasSpecial] = useState<boolean>(false);
  const [hasMinLength, setHasMinLength] = useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [currentPasswordLengthError, setCurrentPasswordLengthError] = useState<string>("");
  const [newPasswordLengthError, setNewPasswordLengthError] = useState<string>("");
  const [confirmPasswordLengthError, setConfirmPasswordLengthError] = useState<string>("");

  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // iOS/Safari leaves the last character revealed when -webkit-text-security is
  // toggled back on (eye off). Forcing a reflow repaints every glyph as a dot.
  const reflow = (el: HTMLInputElement | null) => {
    if (!el) return;
    const prev = el.style.display;
    el.style.display = "none";
    void el.offsetHeight; // trigger reflow
    el.style.display = prev;
  };
  useEffect(() => reflow(currentPasswordRef.current), [showCurrentPassword]);
  useEffect(() => reflow(newPasswordRef.current), [showNewPassword]);
  useEffect(() => reflow(confirmPasswordRef.current), [showConfirmPassword]);

  const handleEditClick = () => setIsEditing(true);

  const handleCancel = () => {
    setTempName(String(displayName));
    setIsEditing(false);

    // Reset password fields
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Strictly prevent input beyond 20 characters
    if (value.length > 20) {
      setError("*Name must be 20 characters or less");
      // Don't update state if it exceeds 20
      return;
    }

    // Validate format
    if (!/^[a-zA-Z\s]*$/.test(value)) {
      setError("*Only letters and spaces are allowed");
    } else {
      setError("");
    }

    setTempName(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    // Prevent typing if already at 20 characters (allow deletion and navigation keys)
    if (
      target.value.length >= 20 &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "Tab" &&
      e.key !== "Home" &&
      e.key !== "End" &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      e.preventDefault();
      setError("*Name must be 20 characters or less");
    }
  };

  const handleNameSave = async () => {
    try {
      await updateDisplayName(String(tempName));
      setIsEditing(false);
      setDisplayName(String(tempName));
      useUserStore.getState().setUsername(tempName);
      useToastStore.getState().showToast("Display name updated successfully!", "success", 3000);
    } catch (error) {
      console.error("Failed to update display name:", error);
      useToastStore.getState().showToast("Failed to update display name.", "error", 3000);
    }
  };

  const handlePasswordSave = async () => {
    if (!currentPassword) {
      useToastStore.getState().showToast("Current password is required.", "error", 3000);
      return;
    }

    if (!isPasswordValid()) {
      useToastStore.getState().showToast("Please meet all password requirements.", "error", 3000);
      return;
    }

    if (newPassword === currentPassword) {
      useToastStore.getState().showToast("New password must be different from current password.", "error", 3000);
      return;
    }

    setIsSavingPassword(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user || !user.email) {
        useToastStore.getState().showToast("User email is not available. Please log in again.", "error", 3000);
        setIsSavingPassword(false);
        return;
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      // Reset fields after success
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      // Changing the password invalidates the session everywhere: end this one
      // and send the admin back to login instead of leaving them signed in.
      forceLogout("Your password was changed. Please sign in again.");
      return;
    } catch (error: any) {
      switch (error.code) {
        case "auth/invalid-credential":
          useToastStore.getState().showToast("Invalid credentials. Please enter the correct current password.", "error", 3000);
          break;
        case "auth/wrong-password":
          useToastStore.getState().showToast("The current password is incorrect.", "error", 3000);
          break;
        case "auth/user-not-found":
          useToastStore.getState().showToast("User account not found. Please log in again.", "error", 3000);
          break;
        case "auth/too-many-requests":
          useToastStore.getState().showToast("Too many failed attempts. Try again later.", "error", 3000);
          break;
        case "auth/requires-recent-login":
          useToastStore.getState().showToast("Your session has expired. Please log in again.", "error", 3000);
          break;
        default:
          useToastStore.getState().showToast("Failed to change password. Please try again.", "error", 3000);
      }
      console.error("Password change error:", error);
    } finally {
      setIsSavingPassword(false);
    }
  };

  useEffect(() => {
    setHasLowercase(/[a-z]/.test(newPassword));
    setHasUppercase(/[A-Z]/.test(newPassword));
    setHasNumber(/[0-9]/.test(newPassword));
    setHasSpecial(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword));
    setHasMinLength(newPassword.length >= 6);
    setPasswordsMatch(newPassword === confirmPassword && newPassword !== "");
  }, [newPassword, confirmPassword]);

  const isPasswordValid = () => {
    return hasLowercase && hasUppercase && hasNumber && hasSpecial && hasMinLength && passwordsMatch && currentPassword.length > 0 && newPassword !== currentPassword;
  };

  return (
    <div>
      <section className="flex justify-between items-center">
        <h1 className="text-xl font-semibold text-grease-100 tracking-tight">Settings</h1>

        <nav className="flex items-center text-sm text-lightgray-200 space-x-2 mt-1">
          <span className="st-dashboard text-lightgray-200 text-sm" aria-hidden="true" />
          <Link to="/admin/dashboard" className="hover:text-royalblue-100 transition-colors duration-150">
            Dashboard
          </Link>
          <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
          <span className="text-royalblue-100 font-medium">Settings</span>
        </nav>
      </section>

      <section className="bg-white p-6 rounded-xl mt-6">
        <div className="grid md:grid-cols-4 gap-6">
          <div>
            <label className="text-xs font-light text-gray-500">Full Name</label>
            <div className="relative">
              <input
                type="text"
                value={String(tempName)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData("text");
                  const currentValue = String(tempName);
                  const newValue = (currentValue + pastedText).substring(0, 20);
                  if (newValue.length > 20) {
                    setError("*Name must be 20 characters or less");
                  } else if (!/^[a-zA-Z\s]*$/.test(newValue)) {
                    setError("*Only letters and spaces are allowed");
                  } else {
                    setError("");
                  }
                  setTempName(newValue);
                }}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  if (target.value.length > 20) {
                    target.value = target.value.substring(0, 20);
                    setError("*Name must be 20 characters or less");
                    setTempName(target.value);
                  }
                }}
                disabled={!isEditing}
                maxLength={20}
                className="block w-full border-none p-4 rounded-lg bg-gray-100 text-sm text-gray-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400 pr-12"
              />
              {isEditing ? (
                <div className="absolute inset-y-0 right-3 flex items-center space-x-2">
                  <button onClick={handleCancel} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                  {tempName && tempName !== displayName && !error && (
                    <button onClick={handleNameSave} className="flex-shrink-0 text-green-500 hover:text-green-700">
                      <Check size={18} />
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleEditClick}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <Pencil size={18} />
                </button>
              )}
            </div>
            {error && <p className="mt-1 ml-1 text-xs text-red-500">{error}</p>}
          </div>
        </div>

        {/* Change Password Section */}
        <p className="text-grease-100 text-md font-medium my-6">Change Password</p>
        {passwordError && <p className="text-red-500 text-sm mb-4">{passwordError}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {/* Current Password Field */}
          <div>
            <label className="block mb-2 text-xs text-lightgray-200">Current Password</label>
            <div className="relative">
              <input
                ref={currentPasswordRef}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={currentPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 20) {
                    setCurrentPasswordLengthError("Password must be 20 characters or less");
                    setCurrentPassword(value.substring(0, 20));
                  } else {
                    setCurrentPasswordLengthError("");
                    setCurrentPassword(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    currentPassword.length >= 20 &&
                    e.key !== "Backspace" &&
                    e.key !== "Delete" &&
                    e.key !== "ArrowLeft" &&
                    e.key !== "ArrowRight" &&
                    e.key !== "Tab" &&
                    e.key !== "Home" &&
                    e.key !== "End" &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.altKey
                  ) {
                    e.preventDefault();
                    setCurrentPasswordLengthError("Password must be 20 characters or less");
                  }
                }}
                minLength={6}
                maxLength={20}
                className={`pw-field ${showCurrentPassword ? "" : "pw-mask"} block w-full border-none p-4 pr-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none`}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-lightgray-200 hover:text-grease-100"
                onClick={() => setShowCurrentPassword((prev) => !prev)}
                aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}
              >
                {showCurrentPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {currentPasswordLengthError && <p className="text-red-500 mt-1 text-xs">{currentPasswordLengthError}</p>}
          </div>
          {/* New Password Field */}
          <div>
            <label className="block mb-2 text-xs text-lightgray-200">New Password</label>
            <div className="relative">
              <input
                ref={newPasswordRef}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={newPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 20) {
                    setNewPasswordLengthError("Password must be 20 characters or less");
                    setNewPassword(value.substring(0, 20));
                  } else {
                    setNewPasswordLengthError("");
                    setNewPassword(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    newPassword.length >= 20 &&
                    e.key !== "Backspace" &&
                    e.key !== "Delete" &&
                    e.key !== "ArrowLeft" &&
                    e.key !== "ArrowRight" &&
                    e.key !== "Tab" &&
                    e.key !== "Home" &&
                    e.key !== "End" &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.altKey
                  ) {
                    e.preventDefault();
                    setNewPasswordLengthError("Password must be 20 characters or less");
                  }
                }}
                minLength={6}
                maxLength={20}
                className={`pw-field ${showNewPassword ? "" : "pw-mask"} block w-full border-none p-4 pr-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none`}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-lightgray-200 hover:text-grease-100"
                onClick={() => setShowNewPassword((prev) => !prev)}
                aria-label={showNewPassword ? "Hide new password" : "Show new password"}
              >
                {showNewPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {newPasswordLengthError && <p className="text-red-500 mt-1 text-xs">{newPasswordLengthError}</p>}
          </div>

          {/* Confirm New Password Field */}
          <div>
            <label className="block mb-2 text-xs text-lightgray-200">Confirm New Password</label>
            <div className="relative">
              <input
                ref={confirmPasswordRef}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={confirmPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 20) {
                    setConfirmPasswordLengthError("Password must be 20 characters or less");
                    setConfirmPassword(value.substring(0, 20));
                  } else {
                    setConfirmPasswordLengthError("");
                    setConfirmPassword(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    confirmPassword.length >= 20 &&
                    e.key !== "Backspace" &&
                    e.key !== "Delete" &&
                    e.key !== "ArrowLeft" &&
                    e.key !== "ArrowRight" &&
                    e.key !== "Tab" &&
                    e.key !== "Home" &&
                    e.key !== "End" &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.altKey
                  ) {
                    e.preventDefault();
                    setConfirmPasswordLengthError("Password must be 20 characters or less");
                  }
                }}
                minLength={6}
                maxLength={20}
                className={`pw-field ${showConfirmPassword ? "" : "pw-mask"} block w-full border-none p-4 pr-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none`}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-lightgray-200 hover:text-grease-100"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirmPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
            {confirmPasswordLengthError && <p className="text-red-500 mt-1 text-xs">{confirmPasswordLengthError}</p>}
          </div>
        </div>

        {newPassword && currentPassword && newPassword === currentPassword && (
          <p className="mt-2 text-xs text-red-500">
            New password must be different from your current password.
          </p>
        )}

        {/* Password Requirements */}
        <div className="flex flex-wrap items-center gap-4 mt-6">
          <div className="flex gap-2 mt-3 items-center">
            <div
              className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasLowercase ? "bg-royalblue-100" : "bg-lightgray-100"}`}
            >
              {hasLowercase && <img src="/images/check.svg" alt="" />}
            </div>
            <p className="text-lightgray-200 font-normal text-xs">One lowercase character</p>
          </div>
          <div className="flex gap-2 mt-3 items-center">
            <div
              className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasUppercase ? "bg-royalblue-100" : "bg-lightgray-100"}`}
            >
              {hasUppercase && <img src="/images/check.svg" alt="" />}
            </div>
            <p className="text-lightgray-200 font-normal text-xs">One uppercase character</p>
          </div>
          <div className="flex gap-2 mt-3 items-center">
            <div
              className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasNumber ? "bg-royalblue-100" : "bg-lightgray-100"}`}
            >
              {hasNumber && <img src="/images/check.svg" alt="" />}
            </div>
            <p className="text-lightgray-200 font-normal text-xs">One number</p>
          </div>
          <div className="flex gap-2 mt-3 items-center">
            <div
              className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasSpecial ? "bg-royalblue-100" : "bg-lightgray-100"}`}
            >
              {hasSpecial && <img src="/images/check.svg" alt="" />}
            </div>
            <p className="text-lightgray-200 font-normal text-xs">One special character</p>
          </div>
          <div className="flex gap-2 mt-3 items-center">
            <div
              className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasMinLength ? "bg-royalblue-100" : "bg-lightgray-100"}`}
            >
              {hasMinLength && <img src="/images/check.svg" alt="" />}
            </div>
            <p className="text-lightgray-200 font-normal text-xs">6 characters minimum</p>
          </div>
          <div className="flex gap-2 mt-3 items-center">
            <div
              className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${passwordsMatch ? "bg-royalblue-100" : "bg-lightgray-100"}`}
            >
              {passwordsMatch && <img src="/images/check.svg" alt="" />}
            </div>
            <p className="text-lightgray-200 font-normal text-xs">Password match</p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-end gap-4">
          <button onClick={handleCancel} className="p-4 w-32 rounded-lg text-lightgray-200 text-center bg-lightgray-100 text-xs mt-6">
            CANCEL
          </button>
          <button
            onClick={handlePasswordSave}
            className={`p-4 w-32 rounded-lg text-white text-center text-xs mt-6 flex items-center justify-center gap-2 transition-opacity ${
              isPasswordValid() && !isSavingPassword ? "bg-[#F5C400]" : "bg-[#F5C400] opacity-70"
            }`}
            disabled={!isPasswordValid() || isSavingPassword}
          >
            {isSavingPassword ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Saving...
              </>
            ) : (
              "SAVE"
            )}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Settings;
